import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  BasisPrice,
  CashPrice,
  Lot,
  MarketType,
  PriceUnion,
  TableLotData,
} from '../lots.model';
import { UserService } from 'src/app/services/user.service';
import { UserDetails } from '../../login/user.model';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { LotDataService } from 'src/app/services/lot-data.service';
import { LotsService } from 'src/app/services/lots.service';
import { SocketService } from 'src/app/services/socket.service';
import { UserState } from 'src/app/store/user/user.reducer';
import {
  changeTableMarket,
  fetchData,
} from 'src/app/store/table-data/table-data.actions';
import { selectLot } from 'src/app/store/lots-library/lot.actions';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { faHandshake } from '@fortawesome/free-regular-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BlockUserRolesDirective } from 'src/app/directives/block-user-roles.directive';
import { DomesticPipe } from 'src/app/pipes/domestic-city.pipe';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { SameYearPipe } from 'src/app/pipes/same-year.pipe';
import { LotDetailsCardComponent } from '../desktop-view/lot-details-card/lot-details-card.component';
import { NegotiationDetailsCardComponent } from '../desktop-view/negotiation-details-card/negotiation-details-card.component';
import { TradesViewerComponent } from '../desktop-view/trades-viewer/trades-viewer.component';
import { selectUser } from 'src/app/store/user/user.selectors';
import { selectTableData } from 'src/app/store/table-data/table-data.selectors';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JCTradingIndicationComponent } from '../../custom/chips/jc-trading-indication/jc-trading-indication.component';

@Component({
  selector: 'app-lot-item',
  templateUrl: './lot-item.component.html',
  styleUrls: ['./lot-item.component.css'],
  standalone: true,
  imports: [
    PriceTypePipe,
    CommonModule,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    BlockUserRolesDirective,
    FontAwesomeModule,
    MatTooltipModule,
    JCTradingIndicationComponent,
  ],
})
export class LotItemComponent implements OnInit, OnDestroy {
  faHandShake = faHandshake;
  lots$: Observable<TableLotData[]>;
  filteredLots = new BehaviorSubject<TableLotData[]>(null);
  filteredLots$ = this.filteredLots.asObservable();
  userDetails: UserDetails;
  userDetails$: Observable<UserDetails>;
  selectedMarket = signal<MarketType | null>(null);
  newLotIds: string[] | null = null;
  isLoading: boolean;
  limit: number = 40;
  buttonOpacity = 1;
  lastScrollTop = 0;
  searchFilter = '';
  private newLotSubscription: Subscription;

  constructor(
    private lotsService: LotsService,
    private lotDataService: LotDataService,
    private socketService: SocketService,
    private userService: UserService,
    private destroyRef: DestroyRef,
    private store: Store<{ user: UserState; lotTableData: TableDataState }>
  ) {}

  onBidsAndOffersScroll(event: any) {
    const currentScroll = event.target.scrollTop;
    const maxScroll = event.target.scrollHeight - event.target.clientHeight;

    if (currentScroll >= maxScroll) {
      this.buttonOpacity = 1; // Scrolled to the bottom, set button opacity to 1
    } else {
      // Check if scrolling down or up
      if (this.lastScrollTop < maxScroll) {
        if (currentScroll > this.lastScrollTop && this.lastScrollTop >= 0) {
          // Scrolling down
          this.buttonOpacity = 0.5;
        } else if (currentScroll < this.lastScrollTop) {
          // Scrolling up
          this.buttonOpacity = 1;
        }
      }
    }

    // Update lastScrollTop for the next scroll event
    this.lastScrollTop = currentScroll;
  }

  ngOnInit() {
    this.isLoading = false;
    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectTableData),
    ])
      .pipe(
        map(([user, tableData]) => {
          if (!this.selectedMarket()) {
            this.store.dispatch(
              fetchData({
                limit: 40,
                marketType: MarketType[user.default_market_type],
              })
            );
          }
          this.selectedMarket.set(tableData.selectedMarket);
          const negotiationLotIds = new Set(
            tableData.negotiations.map((n) => n.lot_id)
          );
          const tableLotData = tableData.lots.map((lot) => ({
            ...lot,
            hasNegotiations: negotiationLotIds.has(lot._id),
          }));
          this.filteredLots.next(tableLotData);
          this.lots$ = of(tableLotData);
          this.store.dispatch(
            selectLot({ lotDetails: { _id: 'none selected' } as Lot }) // just incase?
          );
          if (user) {
            this.userDetails$ = of(user as UserDetails);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Not used?
  getLots(e: MarketType) {
    // console.log('selected option', e);
    this.selectedMarket.set(MarketType[e]);
    this.store.dispatch(fetchData({ limit: 40, marketType: MarketType[e] }));
    this.store.dispatch(changeTableMarket({ marketType: MarketType[e] }));
  }

  ngOnDestroy() {
    if (this.newLotSubscription) {
      this.newLotSubscription.unsubscribe();
    }
  }

  filterList(event) {
    // console.log(event);
    // this.filteredLots$ = this.lots$;
    this.lots$.subscribe((lots) => {
      let filteredData: TableLotData[];
      if (event.selected) {
        if (event.source.ariaLabel == 'Mine') {
          filteredData = lots.filter((e) => e.user_id == this.userDetails._id);
        } else {
          filteredData = lots.filter(
            (e) => e.lot_type == event.source.ariaLabel
          );
        }
      } else {
        filteredData = lots;
      }
      this.filteredLots.next(filteredData);
    });
  }

  searchList(e) {
    const searchValue = e.target.value.toLowerCase();
    this.lots$
      .pipe(
        map((lots) => {
          const filtered = lots.filter((lot) => {
            // Adjust the conditions based on your negotiation model and search requirements
            return (
              (this.getPriceText(lot.price) + '')
                .toLowerCase()
                .includes(searchValue) ||
              lot.commodity.toLowerCase().includes(searchValue) ||
              lot.destination.toLowerCase().includes(searchValue) ||
              lot.grade.toLowerCase().includes(searchValue)
              // TODO : implement search for shipment dates
              // lot.ship_begin.toString().toLowerCase().includes(searchValue)
              // Add more conditions if needed
              // ...
            );
          });
          // console.log('search results', filtered);
          this.filteredLots.next(filtered);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // adds new lot to Lots list
  prependLots(newLots: TableLotData[]) {
    this.lots$ = this.lots$.pipe(
      map((existingLots) => {
        // Filter out new lots that are already in the existing lots
        const filteredNewLots = newLots.filter(
          (newLot) =>
            !existingLots.some((existingLot) => existingLot._id === newLot._id)
        );
        this.newLotIds = filteredNewLots.map((newLot) => {
          if (new Date().getTime() - newLot?.createdAt.getTime() <= 2000) {
            return newLot._id;
          }
        }); // Store the IDs of the new lot

        // Concatenate filtered new lots with existing lots
        return [...filteredNewLots, ...existingLots];
      }),
      takeUntilDestroyed(this.destroyRef)
    );

    setTimeout(() => (this.newLotIds = null), 2000); // Reset after 2 seconds
  }

  getPriceText(price: PriceUnion): number {
    let amount = 0;
    if ('amount' in price) {
      const cashPrice = price as CashPrice;
      amount = cashPrice.amount;
      // console.log('Cash price:', cashPrice.amount);
    } else {
      const basisPrice = price as BasisPrice;
      amount = basisPrice.basisPoints;
      // console.log('Basis price:', basisPrice.month, basisPrice.year);
    }
    return amount;
  }
}
